import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';

const displayValue = (value) => {
    if (Array.isArray(value)) return value.join('\n');
    if (value == null) return '—';
    return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const HomeworkReviewQueueDialog = ({
    open,
    loading,
    items,
    resolvingAnswerId,
    onClose,
    onResolve,
}) => (
    <Dialog open={open} onClose={resolvingAnswerId ? undefined : onClose} fullWidth maxWidth="md">
        <DialogTitle>Homework grading review queue</DialogTitle>
        <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
                These answers received a binary compatibility result, but the automatic decision was not
                trusted. An admin decision becomes the protected final Correct or Incorrect grade.
            </Alert>

            {loading && (
                <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={30} />
                </Box>
            )}

            {!loading && items.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No answers in this submission need review.
                </Typography>
            )}

            {!loading && items.map((item) => {
                const resolving = resolvingAnswerId === item.hw_answer_id;
                return (
                    <Box
                        key={item.hw_answer_id}
                        sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                            <Typography fontWeight="bold">
                                Question {item.question_label || item.hw_question_id}
                            </Typography>
                            <Chip
                                size="small"
                                color={item.grade_status === 'invalid_input' ? 'warning' : 'info'}
                                label={item.reason || item.grade_status}
                            />
                        </Stack>
                        {item.question_content && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {item.question_content}
                            </Typography>
                        )}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" fontWeight="bold">Student answer</Typography>
                                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', m: 0, mt: 0.5 }}>
                                    {displayValue(item.student_answer)}
                                </Box>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" fontWeight="bold">Answer key</Typography>
                                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', m: 0, mt: 0.5 }}>
                                    {displayValue(item.answer_key)}
                                </Box>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                color="error"
                                variant="outlined"
                                disabled={Boolean(resolvingAnswerId)}
                                onClick={() => onResolve(item, false)}
                            >
                                Mark Incorrect
                            </Button>
                            <Button
                                color="success"
                                variant="contained"
                                disabled={Boolean(resolvingAnswerId)}
                                onClick={() => onResolve(item, true)}
                            >
                                {resolving ? 'Saving…' : 'Mark Correct'}
                            </Button>
                        </Stack>
                    </Box>
                );
            })}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={Boolean(resolvingAnswerId)}>Close</Button>
        </DialogActions>
    </Dialog>
);

HomeworkReviewQueueDialog.defaultProps = {
    items: [],
    resolvingAnswerId: null,
};

export default HomeworkReviewQueueDialog;
